import { ColumnDef, CustomTable } from "../../../common/table";
import {
  currency,
  employeePathNames,
  pathNames,
  roleNames,
} from "../../../../constants/constants";
import { IReimbursement } from ".";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import PersonInfo from "../../../common/person-info";
import { DateFormat, formatDate } from "../../../../utils/date-format";
import HistoryModal from "../../../common/modal/HistoryModal";
import {
  HistoryPayload,
  initialHistory,
} from "../../../../apis/history/history.api";
import {
  HistoryFieldEnum,
  RoleEnum,
  statusEnum,
} from "../../../../types/common-types";
import { useAuthStore } from "../../../../store/auth-store";
import StatusCell from "../../../common/status-cell";
import { deleteReimbursement } from "../../../../apis/expense/reimbursement.api";
import ActionModal from "../../../common/modal/ActionModal";

interface IReimbursementListProps {
  reimbursements: IReimbursement[];
  handleUpdateStatus: (value: IReimbursement) => void;
  refreshData: () => void;
}

export default function DepartmentTable({
  reimbursements,
  handleUpdateStatus,
  refreshData,
}: IReimbursementListProps) {
  const { user } = useAuthStore();
  const isEmployee = user.role === RoleEnum.EMPLOYEE;
  const navigate = useNavigate();

  // history states
  const [historyOpen, setHistoryOpen] = useState<boolean>(false);
  const [history, setHistory] = useState<HistoryPayload>(initialHistory);

  const [actionOpen, setActionOpen] = useState<boolean>(false);
  const [expenseId, setExpenseId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const handleAction = (row?: IReimbursement) => {
    console.log("action");
    setActionOpen((prev) => !prev);
    if (row && row?._id) {
      setExpenseId(row._id);
    } else {
      setExpenseId("");
    }
  };

  const handleShowDetails = (reimbursementId: string) => {
    navigate(
      isEmployee
        ? employeePathNames.REIMBURSEMENT_DETAILS
        : pathNames.REIMBURSEMENT_DETAILS,
      {
        state: {
          reimbursementId,
        },
      },
    );
  };
  // Define configuration structures with isolated column custom components
  const columns: ColumnDef<IReimbursement>[] = [
    {
      header: "#",
      className: "",
      render: (_, index) => index + 1,
    },
    {
      header: "Expense Name",
      className: "",
      render: (row) => (
        <div className="flex flex-col">
          <div
            className="text-primary cursor-pointer text-sm font-medium"
            onClick={() => handleShowDetails(row._id)}
          >
            {row.name}
          </div>
          <div className="text-grayText text-xs">{""}</div>
        </div>
      ),
    },
    ...(isEmployee
      ? []
      : [
          {
            header: "Employee Name",
            className: "",
            render: (row: IReimbursement) => (
              <PersonInfo
                personInfo={{
                  profileImage: row.userId.profileImage,
                  firstName: row.userId.firstName,
                  lastName: row.userId.lastName,
                  description: `${row.userId.userId} | ${roleNames[row.userId.role]}`,
                }}
              />
            ),
          },
        ]),
    {
      header: "Expense Date",
      className: "",
      render: (row) => formatDate(row.date),
    },
    {
      header: "Request Date",
      className: "",
      render: (row) => (
        <div>
          <div className="text-gray-600 text-sm">
            {formatDate(row.createdAt)}
          </div>

          <div className="text-xs text-gray-400">
            {formatDate(row.createdAt, DateFormat.TIME_24)}
          </div>
        </div>
      ),
    },
    {
      header: "Amount",
      className: "",
      render: (row) => (
        <span className="font-medium text-secondary">
          {currency.INR} {row.amount}
        </span>
      ),
    },
    {
      header: "Status",
      className: "",
      render: (row) => {
        const isManager =
          row?.userId._id === user._id && user.role === RoleEnum.MANAGER;
        const isDeletable =
          row.userId._id === user._id && row.status === statusEnum.PENDING;
        return (
          <StatusCell
            status={row.status}
            isEditable={!isManager && !isEmployee}
            onHistory={() => handleShowHistory(row)}
            onEdit={() => handleUpdateStatus(row)}
            isDeletable={isDeletable}
            onDelete={() => handleAction(row)}
          />
        );
      },
    },
  ];

  const handleOnConfirm = async () => {
    setLoading(true);
    const response = await deleteReimbursement(expenseId);
    if (response.success) {
      refreshData();
    }
    setLoading(false);
  };

  // handle history open
  const handleHistoryOpenClose = () => {
    setHistoryOpen((prev) => !prev);
    setHistory(initialHistory);
  };

  // handle show history
  const handleShowHistory = (reimbursement: IReimbursement) => {
    handleHistoryOpenClose();
    setHistory({
      field: HistoryFieldEnum.ReimbursementStatus,
      fieldId: reimbursement._id,
      title: reimbursement.name,
    });
  };

  return (
    <>
      <CustomTable columns={columns} data={reimbursements} />
      <HistoryModal
        isOpen={historyOpen}
        handleOpenClose={handleHistoryOpenClose}
        history={history}
        isMailHistory={history.field === HistoryFieldEnum.PromotionMail}
      />
      <ActionModal
        isOpen={actionOpen}
        title={`Are you sure you want to delete this reimbursement?`}
        loading={loading}
        handleOpenClose={handleAction}
        handleSubmit={handleOnConfirm}
      />
    </>
  );
}
